import type { Product } from '@/services/products'
import type { ShopSortMode } from '@/storefront/components/ShopSortToolbar'

/** Single price used for shop filters, URL price bounds, and sort-by-price. */
export function getEffectiveProductPrice(p: Product): number {
  if (p.has_variants && p.min_price != null && Number.isFinite(p.min_price)) {
    return p.min_price
  }
  return typeof p.price === 'number' && Number.isFinite(p.price) ? p.price : 0
}

/** In-stock for catalog list rows (used with `in_stock` URL filter). */
export function isCatalogProductInStock(p: Product): boolean {
  // Dropshipping products do not track local stock. They must stay visible when
  // storefront filters ask for "available" products.
  if (p.is_dropshipping) return true
  if (p.track_inventory === false) return true
  if (p.has_variants) return (p.total_stock ?? 0) > 0
  return p.stock > 0
}

export type SortProductsOptions = {
  /**
   * When true, `featured` leaves the incoming array order unchanged (category PLP).
   * Shop grid uses popularity (`orders_count`) for `featured` when this is false.
   */
  preserveOrderForFeatured?: boolean
}

export function sortProductsByShopMode(
  products: Product[],
  mode: ShopSortMode,
  opts?: SortProductsOptions
): Product[] {
  if (mode === 'featured' && opts?.preserveOrderForFeatured) {
    return [...products]
  }
  const copy = [...products]
  if (mode === 'price-asc') copy.sort((a, b) => getEffectiveProductPrice(a) - getEffectiveProductPrice(b))
  else if (mode === 'price-desc') copy.sort((a, b) => getEffectiveProductPrice(b) - getEffectiveProductPrice(a))
  else if (mode === 'newest')
    copy.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  else copy.sort((a, b) => (b.orders_count ?? 0) - (a.orders_count ?? 0))
  return copy
}
